import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  IL_COLORS,
  formatCurrency,
  formatSignedPercent,
} from "../../../../constants/investmentLeague";

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const SortArrow = ({ direction }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      ml: 0.5,
      fontSize: "10px",
      transform: direction === "asc" ? "rotate(180deg)" : "none",
      transition: "transform 0.15s ease",
    }}
  >
    ▼
  </Box>
);

const RANK_BADGE = {
  1: { bg: "#F5C842", color: "#453400" },
  2: { bg: "#C8CDD4", color: "#2B323D" },
  3: { bg: "#D4956A", color: "#3B2110" },
};

const RankBadge = ({ rank, t }) => {
  const style = RANK_BADGE[rank] ?? {
    bg: "rgba(0, 212, 146, 0.12)",
    color: "#00D492",
    border: "1px solid rgba(0, 212, 146, 0.25)",
  };

  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        background: style.bg,
        color: style.color,
        border: style.border || "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "11px",
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {rank}
    </Box>
  );
};

const getRoiColor = (roi) => {
  if (roi == null || isNaN(roi)) return "inherit";
  if (roi > 0) return IL_COLORS.positive;
  if (roi < 0) return IL_COLORS.negative;
  return "inherit";
};

const cellSx = (align = "left") => ({
  textAlign: align,
  py: 1.25,
  px: { xs: 1, md: 1.5 },
  fontSize: "12px",
  whiteSpace: "nowrap",
});

const RecipientTable = ({
  units = [],
  selectedQuarter = "All Quarters",
  unitType = "All Units",
  onUnitTypeChange,
  selectedUnit,
  onSelectUnit,
  isAdmin = false,
}) => {
  const theme = useTheme();
  const t = theme.custom;

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");

  // Determine active default sort field based on selected quarter
  const effectiveSortField = useMemo(() => {
    if (sortField) return sortField;
    if (selectedQuarter === "Q1") return "q1Investment";
    if (selectedQuarter === "Q2") return "q2Investment";
    if (selectedQuarter === "Q3") return "q3Investment";
    if (selectedQuarter === "Q4") return "q4Investment";
    return "totalInvestment";
  }, [sortField, selectedQuarter]);

  const handleSort = (field) => {
    if (effectiveSortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Filter and sort units
  const processedRows = useMemo(() => {
    let list = [...units];

    // Unit Type Filter
    if (unitType === "MAC") {
      list = list.filter((u) => u.unitType === "MAC");
    } else if (unitType === "Groups") {
      list = list.filter((u) => u.unitType === "Group");
    }

    // Search Filter
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (u) =>
          u.unitName.toLowerCase().includes(q) ||
          u.shortName.toLowerCase().includes(q)
      );
    }

    // Sorting
    list.sort((a, b) => {
      let valA = a[effectiveSortField];
      let valB = b[effectiveSortField];

      if (typeof valA === "string") {
        return sortDirection === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      valA = valA == null ? -Infinity : Number(valA);
      valB = valB == null ? -Infinity : Number(valB);

      return sortDirection === "asc" ? valA - valB : valB - valA;
    });

    return list;
  }, [units, unitType, search, effectiveSortField, sortDirection]);

  return (
    <Box
      sx={{
        background: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      {/* Table Header Controls */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: `1px solid ${t.border}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: t.text }}>
            Investment Recipient Leaderboard
          </Typography>
          <Typography sx={{ fontSize: "12px", color: t.text3, mt: 0.25 }}>
            Ranked by total investment received across selected period ({unitType === "All Units" ? "MACs and Groups" : unitType})
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search MACs or Groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ color: t.text3, display: "flex" }}>
                    <SearchIcon />
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 200 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                fontSize: "12px",
                background: t.surface2,
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={unitType}
              onChange={(e) => onUnitTypeChange?.(e.target.value)}
              sx={{
                borderRadius: "16px",
                fontSize: "11px",
                fontWeight: 600,
                background: t.surface2,
                "& .MuiSelect-select": {
                  py: 0.5,
                  px: 1.5,
                  minHeight: "unset !important",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: t.border2,
                },
              }}
            >
              <MenuItem value="All Units">All Units</MenuItem>
              <MenuItem value="MAC">MAC</MenuItem>
              <MenuItem value="Groups">Groups</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Table Content */}
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            minWidth: "1050px",
            borderCollapse: "collapse",
          }}
        >
          <Box component="thead">
            <Box component="tr" sx={{ background: t.surface2 }}>
              <Box component="th" sx={{ ...cellSx("center"), width: "50px", color: t.text3, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                RANK
              </Box>
              <Box
                component="th"
                onClick={() => handleSort("unitName")}
                sx={{
                  ...cellSx("left"),
                  cursor: "pointer",
                  color: effectiveSortField === "unitName" ? t.text : t.text3,
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  userSelect: "none",
                }}
              >
                UNIT {effectiveSortField === "unitName" && <SortArrow direction={sortDirection} />}
              </Box>
              <Box
                component="th"
                onClick={() => handleSort("unitType")}
                sx={{
                  ...cellSx("center"),
                  cursor: "pointer",
                  color: effectiveSortField === "unitType" ? t.text : t.text3,
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  userSelect: "none",
                }}
              >
                TYPE {effectiveSortField === "unitType" && <SortArrow direction={sortDirection} />}
              </Box>
              <Box
                component="th"
                onClick={() => handleSort("q1Investment")}
                sx={{
                  ...cellSx("right"),
                  cursor: "pointer",
                  color: effectiveSortField === "q1Investment" ? t.text : t.text3,
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  userSelect: "none",
                }}
              >
                Q1 INVESTMENT {effectiveSortField === "q1Investment" && <SortArrow direction={sortDirection} />}
              </Box>
              <Box component="th" sx={{ ...cellSx("right"), color: t.text3, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Q1 ROI
              </Box>
              <Box
                component="th"
                onClick={() => handleSort("q2Investment")}
                sx={{
                  ...cellSx("right"),
                  cursor: "pointer",
                  color: effectiveSortField === "q2Investment" ? t.text : t.text3,
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  userSelect: "none",
                }}
              >
                Q2 INVESTMENT {effectiveSortField === "q2Investment" && <SortArrow direction={sortDirection} />}
              </Box>
              <Box component="th" sx={{ ...cellSx("right"), color: t.text3, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Q2 ROI
              </Box>
              <Box
                component="th"
                onClick={() => handleSort("q3Investment")}
                sx={{
                  ...cellSx("right"),
                  cursor: "pointer",
                  color: effectiveSortField === "q3Investment" ? t.text : t.text3,
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  userSelect: "none",
                }}
              >
                Q3 INVESTMENT {effectiveSortField === "q3Investment" && <SortArrow direction={sortDirection} />}
              </Box>
              <Box component="th" sx={{ ...cellSx("right"), color: t.text3, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Q3 ROI
              </Box>
              <Box
                component="th"
                onClick={() => handleSort("q4Investment")}
                sx={{
                  ...cellSx("right"),
                  cursor: "pointer",
                  color: effectiveSortField === "q4Investment" ? t.text : t.text3,
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  userSelect: "none",
                }}
              >
                Q4 INVESTMENT {effectiveSortField === "q4Investment" && <SortArrow direction={sortDirection} />}
              </Box>
              <Box component="th" sx={{ ...cellSx("right"), color: t.text3, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Q4 ROI
              </Box>
              <Box
                component="th"
                onClick={() => handleSort("totalInvestment")}
                sx={{
                  ...cellSx("right"),
                  cursor: "pointer",
                  color: effectiveSortField === "totalInvestment" ? t.text : t.text3,
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  userSelect: "none",
                }}
              >
                TOTAL INVESTMENT {effectiveSortField === "totalInvestment" && <SortArrow direction={sortDirection} />}
              </Box>
              <Box
                component="th"
                onClick={() => handleSort("overallRoi")}
                sx={{
                  ...cellSx("right"),
                  cursor: "pointer",
                  color: effectiveSortField === "overallRoi" ? t.text : t.text3,
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  userSelect: "none",
                }}
              >
                OVERALL ROI {effectiveSortField === "overallRoi" && <SortArrow direction={sortDirection} />}
              </Box>
            </Box>
          </Box>

          <Box component="tbody">
            {processedRows.map((row, index) => {
              const isSelected = selectedUnit?.unitName === row.unitName;
              const displayRank = index + 1;

              const canSelect = isAdmin && Boolean(onSelectUnit);

              return (
                <Box
                  component="tr"
                  key={row.unitName}
                  onClick={canSelect ? () => onSelectUnit?.(isSelected ? null : row) : undefined}
                  sx={{
                    background: isSelected ? "rgba(0, 212, 146, 0.08)" : "transparent",
                    borderBottom: `1px solid ${t.border}`,
                    borderLeft: isSelected ? "3px solid #00D492" : "3px solid transparent",
                    cursor: canSelect ? "pointer" : "default",
                    transition: "background 0.1s ease",
                    "&:hover": {
                      background: isSelected
                        ? "rgba(0, 212, 146, 0.12)"
                        : canSelect
                          ? t.surface2
                          : "transparent",
                    },
                  }}
                >
                  {/* Rank */}
                  <Box component="td" sx={cellSx("center")}>
                    <RankBadge rank={displayRank} t={t} />
                  </Box>

                  {/* Unit */}
                  <Box component="td" sx={cellSx("left")}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: t.text }}>
                      {row.unitName}
                    </Typography>
                  </Box>

                  {/* Type */}
                  <Box component="td" sx={cellSx("center")}>
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 1,
                        py: 0.2,
                        borderRadius: "4px",
                        background: row.unitType === "MAC" ? "rgba(0, 212, 146, 0.10)" : "rgba(56, 189, 248, 0.10)",
                        border: `1px solid ${row.unitType === "MAC" ? "rgba(0, 212, 146, 0.25)" : "rgba(56, 189, 248, 0.25)"}`,
                        fontSize: "10.5px",
                        fontWeight: 600,
                        color: row.unitType === "MAC" ? "#00D492" : "#38BDF8",
                      }}
                    >
                      {row.unitType}
                    </Box>
                  </Box>

                  {/* Q1 Investment */}
                  <Box component="td" sx={{ ...cellSx("right"), color: t.text2 }}>
                    {formatCurrency(row.q1Investment)}
                  </Box>

                  {/* Q1 ROI */}
                  <Box
                    component="td"
                    sx={{
                      ...cellSx("right"),
                      color: getRoiColor(row.q1Roi),
                      fontWeight: row.q1Roi ? 600 : 400,
                    }}
                  >
                    {row.q1Investment > 0 ? formatSignedPercent(row.q1Roi) : "-"}
                  </Box>

                  {/* Q2 Investment */}
                  <Box component="td" sx={{ ...cellSx("right"), color: t.text2 }}>
                    {formatCurrency(row.q2Investment)}
                  </Box>

                  {/* Q2 ROI */}
                  <Box
                    component="td"
                    sx={{
                      ...cellSx("right"),
                      color: getRoiColor(row.q2Roi),
                      fontWeight: row.q2Roi ? 600 : 400,
                    }}
                  >
                    {row.q2Investment > 0 ? formatSignedPercent(row.q2Roi) : "-"}
                  </Box>

                  {/* Q3 Investment */}
                  <Box component="td" sx={{ ...cellSx("right"), color: t.text2 }}>
                    {formatCurrency(row.q3Investment)}
                  </Box>

                  {/* Q3 ROI */}
                  <Box
                    component="td"
                    sx={{
                      ...cellSx("right"),
                      color: getRoiColor(row.q3Roi),
                      fontWeight: row.q3Roi ? 600 : 400,
                    }}
                  >
                    {row.q3Investment > 0 ? formatSignedPercent(row.q3Roi) : "-"}
                  </Box>

                  {/* Q4 Investment */}
                  <Box component="td" sx={{ ...cellSx("right"), color: t.text2 }}>
                    {formatCurrency(row.q4Investment)}
                  </Box>

                  {/* Q4 ROI */}
                  <Box
                    component="td"
                    sx={{
                      ...cellSx("right"),
                      color: getRoiColor(row.q4Roi),
                      fontWeight: row.q4Roi ? 600 : 400,
                    }}
                  >
                    {row.q4Investment > 0 ? formatSignedPercent(row.q4Roi) : "-"}
                  </Box>

                  {/* Total Investment */}
                  <Box component="td" sx={{ ...cellSx("right"), color: t.text, fontWeight: 600 }}>
                    {formatCurrency(row.totalInvestment)}
                  </Box>

                  {/* Overall ROI */}
                  <Box
                    component="td"
                    sx={{
                      ...cellSx("right"),
                      color: getRoiColor(row.overallRoi),
                      fontWeight: 700,
                    }}
                  >
                    {formatSignedPercent(row.overallRoi)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RecipientTable;

